// src/pages/PreCottageList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import Select from "./ui/Select";
import PaginationControls from "../components/PaginationControls";
import { formatNumber } from "../utils/numberFormat";
import HotToastMessage from "../components/ui/HotToastMessage";
import ConfirmModal from "../components/ui/ConfirmModal";
import axiosInstance from "../utils/axiosInstance";
import {
  MoveDown,
  MoveUp,
  Filter,
  Loader2,
  Search,
  Trash2,
  X,
} from "lucide-react";

// Reusable slide-over (same one you used in CottageList)
import FilterSlideOver, {
  AccordionItem,
} from "../components/ui/FilterSlideOver";

export const PreCottageList = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (auth.isAuthenticated === false) navigate("/");
  }, [auth.isAuthenticated, navigate]);

  // ---- data state
  const [preCottages, setPreCottages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  // ---- ui / filters
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");

  const [orderNumberFilter, setOrderNumberFilter] = useState("");
  const [orderNumbers, setOrderNumbers] = useState([]);
  const [loadingOrderNumbers, setLoadingOrderNumbers] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currency, setCurrency] = useState("");

  const [selectedPreCottages, setSelectedPreCottages] = useState([]);
  const [sortConfig, setSortConfig] = useState({ ordering: "" });

  // ---- pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // ---- toast / modal
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [showModal, setShowModal] = useState(false);

  // ---- currency options
  const currencyOptions = useMemo(
    () => [
      { value: "", label: "همه" },
      { value: "یورو", label: "یورو" },
      { value: "دلار", label: "دلار" },
      { value: "ریال", label: "ریال" },
      { value: "یوان", label: "یوان" },
      { value: "درهم", label: "درهم" },
    ],
    []
  );

  // ---- order numbers
  useEffect(() => {
    const fetchOrderNumbers = async () => {
      setLoadingOrderNumbers(true);
      try {
        const res = await axiosInstance.get("/performas/?page_size=1000");
        const list = Array.isArray(res.data?.results) ? res.data.results : [];
        const numbers = list.map((i) => i?.prf_order_no).filter(Boolean);
        setOrderNumbers(numbers);
      } catch (err) {
        console.error("خطا در بارگذاری شماره ثبت سفارش‌ها:", err);
        setOrderNumbers([]);
        setToast({
          message: "خطا در بارگذاری شماره‌های ثبت سفارش",
          type: "error",
        });
      } finally {
        setLoadingOrderNumbers(false);
      }
    };
    fetchOrderNumbers();
  }, []);

  const orderNumberOptions = useMemo(
    () => [
      { value: "", label: "همه شماره‌های ثبت سفارش" },
      ...orderNumbers.map((num) => ({ value: num, label: num })),
    ],
    [orderNumbers]
  );

  // ---- debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // ---- fetch list
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          search: query,
          proforma: orderNumberFilter, // keep your API contract
          start_date: startDate,
          end_date: endDate,
          currency: currency,
          page: currentPage,
          page_size: pageSize,
          ...(sortConfig.ordering && { ordering: sortConfig.ordering }),
        };

        const res = await axiosInstance.get("/precottages/", { params });
        setPreCottages(res.data?.results || []);
        setCount(res.data?.count || 0);
        setNext(res.data?.next || null);
        setPrevious(res.data?.previous || null);
      } catch (err) {
        console.error("Error fetching pre-cottages:", err);
        setError("خطا در بارگذاری داده‌ها");
        setToast({ message: "خطا در بارگذاری داده‌ها", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    currentPage,
    pageSize,
    query,
    orderNumberFilter,
    startDate,
    endDate,
    currency,
    sortConfig.ordering,
  ]);

  // ---- date handlers
  const handleStartDateChange = (date) => {
    if (date && date.format) setStartDate(date.format("YYYY-MM-DD"));
    else setStartDate("");
  };

  const handleEndDateChange = (date) => {
    if (date && date.format) setEndDate(date.format("YYYY-MM-DD"));
    else setEndDate("");
  };

  // ---- sort
  const handleSort = (field) => {
    setSortConfig((prev) => {
      const isDescending = prev.ordering === field;
      return { ordering: isDescending ? `-${field}` : field };
    });
    setCurrentPage(1);
  };

  // ---- pagination
  const totalPages = count ? Math.ceil(count / pageSize) : 1;
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // ---- selection
  const handleSelectPreCottage = (event, preCottage) => {
    const { checked } = event.target;
    const code = preCottage.co_code;

    setSelectedPreCottages((prev) =>
      checked ? [...prev, code] : prev.filter((x) => x !== code)
    );
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setSelectedPreCottages(checked ? preCottages.map((p) => p.co_code) : []);
  };

  // ---- delete flow
  const handleDeleteSelected = () => {
    if (!selectedPreCottages.length) {
      setToast({
        message: "لطفاً حداقل یک پیش‌سفارش را انتخاب کنید.",
        type: "warn",
      });
      return;
    }
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowModal(false);
    try {
      await axiosInstance.delete("/precottages/bulk-delete/", {
        data: { co_codes: selectedPreCottages },
      });

      setToast({
        message: "پیش‌سفارش‌های انتخاب شده با موفقیت حذف شدند.",
        type: "success",
      });
      setSelectedPreCottages([]);

      // refetch current page
      const params = {
        search: query,
        proforma: orderNumberFilter,
        start_date: startDate,
        end_date: endDate,
        currency: currency,
        page: currentPage,
        page_size: pageSize,
        ...(sortConfig.ordering && { ordering: sortConfig.ordering }),
      };
      const res = await axiosInstance.get("/precottages/", { params });
      setPreCottages(res.data?.results || []);
      setCount(res.data?.count || 0);
      setNext(res.data?.next || null);
      setPrevious(res.data?.previous || null);
    } catch (err) {
      console.error("Error deleting pre-cottages:", err);
      setToast({ message: "خطا در حذف پیش‌سفارش‌ها.", type: "error" });
    }
  };

  const handleCancelDelete = () => setShowModal(false);

  // ---- clear filters
  const clearFilters = () => {
    setOrderNumberFilter("");
    setStartDate("");
    setEndDate("");
    setCurrency("");
    setSearchText("");
    setCurrentPage(1);
  };

  // ---- active filters count + chips
  const activeFiltersCount = useMemo(
    () =>
      [query, orderNumberFilter, startDate, endDate, currency].filter(Boolean)
        .length,
    [query, orderNumberFilter, startDate, endDate, currency]
  );

  const filterChips = useMemo(() => {
    const chips = [];
    if (query) chips.push({ k: "جستجو", v: query });
    if (orderNumberFilter) chips.push({ k: "ثبت سفارش", v: orderNumberFilter });
    if (startDate) chips.push({ k: "از تاریخ", v: startDate });
    if (endDate) chips.push({ k: "تا تاریخ", v: endDate });
    if (currency) chips.push({ k: "ارز", v: currency });
    return chips;
  }, [query, orderNumberFilter, startDate, endDate, currency]);

  // ---- react-select styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "rgba(255,255,255,0.95)",
      borderColor: "#E5E7EB",
      borderRadius: "0.75rem",
      padding: "0.25rem",
      boxShadow: "none",
      textAlign: "right",
      minHeight: "44px",
      ":hover": { borderColor: "#2563EB" },
    }),
    menu: (base) => ({ ...base, textAlign: "right", zIndex: 60 }),
    option: (base, state) => ({
      ...base,
      textAlign: "right",
      backgroundColor: state.isSelected
        ? "#2563EB"
        : state.isFocused
        ? "#DBEAFE"
        : "#fff",
      color: state.isSelected ? "#fff" : "#111827",
    }),
    singleValue: (base) => ({ ...base, textAlign: "right" }),
    placeholder: (base) => ({ ...base, textAlign: "right" }),
  };

  return (
    <div className="mt-24 pb-20 bg-gray-50 min-h-screen font-vazir" dir="rtl">
      {/* Toast & Confirm */}
      <HotToastMessage
        message={toast.message}
        type={toast.type}
        duration={3000}
        onClose={() => setToast({ message: "", type: "info" })}
        position="top"
      />
      <ConfirmModal
        isOpen={showModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Container */}
      <div className="mx-auto max-w-7xl md:max-w-[90%] p-4 sm:p-6 bg-white/70 backdrop-blur-md rounded-2xl shadow-sm border border-white/30">
        {/* Top bar */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1 text-right">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              لیست پیش‌سفارش‌های ثبت‌شده
            </h2>
            <p className="text-gray-500 text-sm">
              تعیین منشأ ارز، فیلتر، انتخاب گروهی و حذف
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
            >
              <Filter className="w-4 h-4" />
              فیلترها
              {activeFiltersCount > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-xl border hover:bg-gray-50 transition"
              >
                <X className="w-4 h-4" />
                پاک‌سازی همه
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 text-right mb-2">
            جستجو (شماره پیش‌سفارش/نام فروشنده/وضعیت):
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="جستجوی خود را وارد کنید…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-3 pr-10 py-3 rounded-xl border bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-right"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>

          {/* Chips */}
          {filterChips.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {filterChips.map((c, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs border border-blue-100"
                >
                  <b className="font-medium">{c.k}:</b> {c.v}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={handleDeleteSelected}
            disabled={!selectedPreCottages.length}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            حذف انتخابی‌ها
          </button>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="mt-8 flex items-center justify-center gap-2 text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" />
            در حال بارگذاری…
          </div>
        )}
        {error && !loading && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {/* Table & Pagination */}
        {!loading && !error && (
          <>
            {preCottages.length === 0 ? (
              <div className="mt-10 text-center text-gray-600">
                هیچ پیش‌سفارشی یافت نشد.
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 50, 100, 200]}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>

                <div className="mt-3 overflow-x-auto rounded-2xl border bg-white shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-right text-gray-700">
                        <th className="p-3">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              selectedPreCottages.length ===
                                preCottages.length && preCottages.length > 0
                            }
                          />
                        </th>
                        <th className="p-3 font-semibold">ردیف</th>
                        <th className="p-3 font-semibold">کد یکتا منشا ارز</th>
                        <th className="p-3 font-semibold">شماره ثبت سفارش</th>

                        <th
                          className="p-3 font-semibold cursor-pointer select-none"
                          onClick={() => handleSort("co_date")}
                          title="مرتب‌سازی"
                        >
                          <span className="inline-flex items-center gap-1">
                            تاریخ منشا ارز
                            {sortConfig.ordering === "co_date" && (
                              <MoveUp className="w-4 h-4 text-blue-600" />
                            )}
                            {sortConfig.ordering === "-co_date" && (
                              <MoveDown className="w-4 h-4 text-blue-600" />
                            )}
                          </span>
                        </th>

                        <th
                          className="p-3 font-semibold cursor-pointer select-none"
                          onClick={() => handleSort("co_clearence_date")}
                          title="مرتب‌سازی"
                        >
                          <span className="inline-flex items-center gap-1">
                            تاریخ اعتبار
                            {sortConfig.ordering === "co_clearence_date" && (
                              <MoveUp className="w-4 h-4 text-blue-600" />
                            )}
                            {sortConfig.ordering === "-co_clearence_date" && (
                              <MoveDown className="w-4 h-4 text-blue-600" />
                            )}
                          </span>
                        </th>

                        <th
                          className="p-3 font-semibold cursor-pointer select-none"
                          onClick={() => handleSort("total_value")}
                          title="مرتب‌سازی"
                        >
                          <span className="inline-flex items-center gap-1">
                            مبلغ کل
                            {sortConfig.ordering === "total_value" && (
                              <MoveUp className="w-4 h-4 text-blue-600" />
                            )}
                            {sortConfig.ordering === "-total_value" && (
                              <MoveDown className="w-4 h-4 text-blue-600" />
                            )}
                          </span>
                        </th>

                        <th
                          className="p-3 font-semibold cursor-pointer select-none"
                          onClick={() => handleSort("co_fob")}
                          title="مرتب‌سازی"
                        >
                          <span className="inline-flex items-center gap-1">
                            FOB
                            {sortConfig.ordering === "co_fob" && (
                              <MoveUp className="w-4 h-4 text-blue-600" />
                            )}
                            {sortConfig.ordering === "-co_fob" && (
                              <MoveDown className="w-4 h-4 text-blue-600" />
                            )}
                          </span>
                        </th>

                        <th className="p-3 font-semibold">وضعیت</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>

                    <tbody>
                      {preCottages.map((p, index) => (
                        <tr
                          key={p.id}
                          className="border-t last:border-b-0 hover:bg-gray-50"
                        >
                          <td className="p-3 align-middle">
                            <input
                              type="checkbox"
                              checked={selectedPreCottages.includes(p.co_code)}
                              onChange={(e) => handleSelectPreCottage(e, p)}
                            />
                          </td>

                          <td className="p-3 align-middle">
                            {index + 1 + (currentPage - 1) * pageSize}
                          </td>

                          <td className="p-3 align-middle">
                            <Link
                              className="text-blue-600 hover:underline"
                              to={`/precottagesbynumber/${p.co_code}`}
                            >
                              {p.co_code}
                            </Link>
                          </td>

                          <td className="p-3 align-middle">
                            <Link
                              className="text-blue-600 hover:underline"
                              to={`/order-details/${p.proforma_vcode}`}
                            >
                              {p.proforma_order_no}
                            </Link>
                          </td>

                          <td className="p-3 align-middle">{p.co_date}</td>
                          <td className="p-3 align-middle">
                            {p.co_clearence_date}
                          </td>

                          <td className="p-3 align-middle">
                            {formatNumber(p.total_value)}
                          </td>

                          <td className="p-3 align-middle">
                            {Number(p.co_fob) >= 0
                              ? `باقی مانده : ${formatNumber(p.co_fob)}`
                              : `مابع تفاوت : ${formatNumber(
                                  Math.abs(p.co_fob)
                                )}`}
                          </td>

                          <td className="p-3 align-middle">
                            {p.co_status || "—"}
                          </td>
                          <td className="p-3 align-middle"></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 50, 100, 200]}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onPageChange={handlePageChange}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Sticky bulk bar */}
      {selectedPreCottages.length > 0 && (
        <div className="fixed left-0 right-0 bottom-4 z-20 px-4">
          <div className="mx-auto max-w-[900px] rounded-2xl border bg-white/90 backdrop-blur p-3 shadow-lg">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-gray-700">
                <b>{selectedPreCottages.length}</b> مورد انتخاب شده
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-xl hover:bg-red-700 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide-Over Filters */}
      <FilterSlideOver
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلترها"
        badgeCount={activeFiltersCount}
        onClear={clearFilters}
        onApply={() => setFiltersOpen(false)}
        dir="rtl"
      >
        <AccordionItem title="سفارش" defaultOpen>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 text-right">
              شماره ثبت سفارش:
            </label>
            <Select
              value={
                orderNumberOptions.find(
                  (opt) => opt.value === orderNumberFilter
                ) || null
              }
              onChange={(opt) => setOrderNumberFilter(opt ? opt.value : "")}
              options={orderNumberOptions}
              isClearable
              isLoading={loadingOrderNumbers}
              placeholder={
                loadingOrderNumbers ? "در حال بارگذاری..." : "همه شماره‌ها"
              }
              styles={selectStyles}
              className="text-sm"
              noOptionsMessage={() =>
                loadingOrderNumbers
                  ? "در حال بارگذاری..."
                  : orderNumbers.length === 0
                  ? "شماره ثبت سفارشی یافت نشد"
                  : "شماره‌ای یافت نشد"
              }
            />
          </div>
        </AccordionItem>

        <AccordionItem title="تاریخ‌ها" defaultOpen>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-right">
                تاریخ شروع:
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.25rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "0.75rem",
                  height: "2.8rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
                value={startDate}
                onChange={handleStartDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ شروع"
                className="w-full"
                clearable
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-right">
                تاریخ پایان:
              </label>
              <DatePicker
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1.25rem",
                  width: "100%",
                  borderColor: "#E2E8F0",
                  borderRadius: "0.75rem",
                  height: "2.8rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
                value={endDate}
                onChange={handleEndDateChange}
                calendar={persian}
                locale={persian_fa}
                format="YYYY-MM-DD"
                numerals="en"
                placeholder="تاریخ پایان"
                className="w-full"
                clearable
              />
            </div>
          </div>
        </AccordionItem>

        <AccordionItem title="ارز" defaultOpen>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 text-right">
              ارز:
            </label>
            <Select
              value={
                currencyOptions.find((opt) => opt.value === currency) || null
              }
              onChange={(opt) => setCurrency(opt ? opt.value : "")}
              options={currencyOptions}
              isClearable
              placeholder="همه ارزها"
              styles={selectStyles}
              className="text-sm"
            />
          </div>
        </AccordionItem>
      </FilterSlideOver>
    </div>
  );
};

export default PreCottageList;
